import { FastifyRequest } from "fastify";
import React from "react";
import styles from "./page.module.css";
import LayoutUsuario from "../../components/navegacao/LayoutUsuario";
import Titulo from "../../components/tipografia/Titulo";
import Descricao from "../../components/tipografia/Descricao";
import HBox from "../../components/layout/HBox";
import VBox from "../../components/layout/VBox";
import Botao from "../../components/Botao";
import Grid, { GridItem } from "../../components/layout/Grid";
import CampoTexto from "../../components/Formulario/CampoTexto";
import DataTable, { DataTableHeader } from "../../components/layout/DataTable";
import Icon from "../../components/Icon";
import {
    mdiArrowLeft,
    mdiLoading,
    mdiMagnify,
    mdiPen,
    mdiPlus,
    mdiRefresh,
    mdiTrashCan,
    mdiApi,
    mdiConnection,
    mdiWeb,
    mdiCodeTagsCheck,
    mdiShapeOutline,
    mdiListBox,
    mdiFile,
    mdiFileDocument,
    mdiCircle,
    mdiLanCheck,
    mdiLanDisconnect,
    mdiPlayCircleOutline
} from "@mdi/js";
import { useMutation, useInfiniteQuery, useQuery } from "react-query";
import axios from "axios";
import { useDebounce } from "../chat/useDebounce";
import { toast } from "react-toastify";
import Modal from "../../components/layout/Modal";
import BotaoPerigoso from "../../components/BotaoPerigoso";
import Card from "../../components/layout/Card";
import Badge from "../../components/layout/Badge";
import { FormIntegracaoApi } from "./FormIntegracaoApi";
import { IntegrationLogsModal } from "./IntegrationLogsModal";
import { IntegrationTestActionModal } from "./IntegrationTestActionModal";
import FreeTierModal from "@/components/FreeTierModal";

type PageProps = {};

type ResponseList = {
    status: number;
    ok?: boolean;
    result: any[];
    total: number;
    cursor: string | number | null;
};

/**
 * Componente de card para exibir templates disponíveis
 */

/**
 * Componente para seleção de template
 */


/**
 * Página principal de integração
 */
const PageIntegracao: React.FC<PageProps> = (props) => {
    const [search, setSearch] = React.useState<string>("");
    const debouncedSearch = useDebounce(search, 500);

    const [editObject, setEditObject] = React.useState<any>(null);
    const [availableTemplates, setAvailableTemplates] = React.useState<any[]>([]);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string> | null>(null);

    // Estado para controlar a exibição do modal de logs
    const [showLogsModal, setShowLogsModal] = React.useState(false);
    const [selectedIntegrationId, setSelectedIntegrationId] = React.useState<number | null>(null);

    // Estado de ping por integração: 'idle' | 'loading' | número (latência ms) | 'error'
    const [pingState, setPingState] = React.useState<Record<number, 'loading' | number | 'error'>>({});

    // Estado para o modal de teste de ações
    const [showTestActionModal, setShowTestActionModal] = React.useState(false);
    const [testActionIntegration, setTestActionIntegration] = React.useState<any>(null);


    // Buscar capacidades do plano
    const capacidadesQuery = useQuery(
        ['capacidades'],
        async () => {
            const res = await axios.get('/api/capacidades', {
                withCredentials: true,
            });
            return res.data;
        },
        {
            refetchOnWindowFocus: false,
            staleTime: 30000, // 30 segundos
        }
    );

    // Carrega lista de templates disponíveis
    React.useEffect(() => {
        axios
            .get("/api/integrations/templates")
            .then((response) => {
                if (response.data?.ok && response.data?.result) {
                    setAvailableTemplates(response.data.result);
                }
            })
            .catch((error) => {
                console.error("Erro ao buscar templates:", error);
            });
    }, []);

    // Consulta lista de integrações
    const queryList = useInfiniteQuery<ResponseList>(
        ["integrations", debouncedSearch],
        async ({ pageParam = 0 }) => {
            const params = new URLSearchParams({
                c: pageParam.toString(),
                txt: debouncedSearch,
            });

            const response = await axios.get<ResponseList>(
                `/api/integrations?${params.toString()}`,
                { withCredentials: true }
            );
            return response.data;
        },
        {
            getNextPageParam: (lastPage) => lastPage.cursor,
        }
    );

    // Mutation para criar/atualizar
    const changeMutator = useMutation(
        async () => {
            if (!editObject) return null;

            if (editObject.id > 0) {
                return await axios.put(`/api/integrations/${editObject.id}`, editObject);
            } else {
                return await axios.post("/api/integrations", editObject);
            }
        },
        {
            onSuccess: () => {
                toast("Integração salva com sucesso", {
                    toastId: "success",
                    position: "top-center",
                    type: "success",
                    autoClose: 5000,
                });
                setEditObject(null);
                setFieldErrors(null);
                queryList.refetch();
            },
            onError: (err: any) => {
                console.error(err);

                if (err.response?.data?.fieldErrors) {
                    setFieldErrors(err.response.data.fieldErrors);
                } else {
                    toast(err.response?.data?.message || "Erro ao salvar integração", {
                        toastId: "error",
                        position: "top-center",
                        type: "error",
                        autoClose: 5000,
                    });
                }
            },
        }
    );

    // Mutation para excluir
    const deleteMutator = useMutation(
        async (id: number) => {
            return await axios.delete(`/api/integrations/${id}`);
        },
        {
            onSuccess: () => {
                toast("Integração removida com sucesso", {
                    toastId: "success",
                    position: "top-center",
                    type: "success",
                    autoClose: 5000,
                });
                queryList.refetch();
            },
            onError: (err: any) => {
                console.error(err);
                toast("Erro ao remover integração", {
                    toastId: "error",
                    position: "top-center",
                    type: "error",
                    autoClose: 5000,
                });
            },
        }
    );

    // Handler para criar nova integração
    const handlePing = async (row: any) => {
        setPingState(prev => ({ ...prev, [row.id]: 'loading' }));
        try {
            const res = await axios.post(`/api/integrations/${row.id}/ping`, {}, { withCredentials: true });
            const latency: number = res.data?.result?.latency ?? 0;
            setPingState(prev => ({ ...prev, [row.id]: latency }));
            toast(`Ping: ${latency}ms`, {
                toastId: `ping-${row.id}`,
                position: 'top-center',
                type: 'success',
                autoClose: 3000,
            });
        } catch (err: any) {
            setPingState(prev => ({ ...prev, [row.id]: 'error' }));
            toast(err.response?.data?.error || 'Ping falhou — adapter não respondeu', {
                toastId: `ping-err-${row.id}`,
                position: 'top-center',
                type: 'error',
                autoClose: 5000,
            });
        }
    };

    const disconnectMutator = useMutation(
        async (id: number) => axios.post(`/api/integrations/${id}/disconnect`, {}, { withCredentials: true }),
        {
            onSuccess: () => {
                toast('Adapter desconectado. Ele deverá reconectar automaticamente.', {
                    toastId: 'disconnect-ok',
                    position: 'top-center',
                    type: 'info',
                    autoClose: 5000,
                });
                setTimeout(() => queryList.refetch(), 2000);
            },
            onError: (err: any) => {
                toast(err.response?.data?.error || 'Erro ao desconectar', {
                    toastId: 'disconnect-err',
                    position: 'top-center',
                    type: 'error',
                    autoClose: 5000,
                });
            },
        }
    );

    const handleNewIntegration = () => {
        if (capacidadesQuery?.data?.freeTier) {
            setShowFreeTierModal(true);
            return;
        }
        // Inicializa o objeto editável e mostra o modal de seleção de template
        console.log("Iniciando criação de nova integração");
        setEditObject({});
    };

    // Handler para alterar versão de uma integração
    const handleChangeVersion = (integration: any) => {
        // TODO: Implementar modal para seleção de versão
    };

    // Handler para editar
    const handleEdit = (integration: any) => {
        setEditObject(integration);
    };

    // Handler para excluir
    const handleDelete = (id: number) => {
        if (confirm("Tem certeza que deseja excluir esta integração?")) {
            deleteMutator.mutate(id);
        }
    };

    // Handler para salvar
    const handleSave = () => {
        changeMutator.mutate();
    };

    // Monta lista de integrações
    const integrations = React.useMemo(() => {
        return queryList.data?.pages.flatMap((page) => page.result) || [];
    }, [queryList.data]);

    const headers: DataTableHeader[] = [
        {
            label: "Nome",
            width: "100%",
            render: (row: any) => (
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ color: row.connected ? "var(--tc-color-success-300)" : "var(--tc-color-danger-300)", marginRight: "8px" }} title={
                        row.connected ? "Conectado" : "Desconectado"
                    }>
                        <Icon path={mdiCircle} size={0.5} />
                    </span>
                    <span style={{ fontWeight: 500, color: row.active ? "var(--tc-color-text)" : "var(--tc-color-text-disabled)" }}>{row.name}</span>
                    {row.connected && row.version && (
                        <span style={{ marginLeft: "8px", fontSize: "0.75em", color: "var(--tc-color-text-secondary)", opacity: 0.7 }}>
                            v{row.version}
                        </span>
                    )}
                </div>
            ),
        },
        {
            label: "Ações",
            width: "200px",
            stickRight: true,
            alignRight: true,
            render: (row: any) => (
                <HBox style={{ gap: "8px", justifyContent: "flex-end" }}>
                    {row.connected && row.template_key && row.template_key !== 'custom' && (<>
                        {availableTemplates.find(t => t.key === row.template_key)?.hasAdapter !== false && (<>
                            <Botao
                                variant="none-success"
                                icon={pingState[row.id] === 'loading' ? mdiLoading : mdiLanCheck}
                                title={pingState[row.id] === 'loading'
                                    ? 'Aguardando...'
                                    : typeof pingState[row.id] === 'number'
                                        ? `Último ping: ${pingState[row.id]}ms — Testar novamente`
                                        : pingState[row.id] === 'error'
                                            ? 'Ping falhou — Tentar novamente'
                                            : 'Testar conectividade (ping)'}
                                onClick={() => handlePing(row)}
                                disabled={pingState[row.id] === 'loading' || !row.connected}
                            />
                            <BotaoPerigoso
                                variant="none-warning"
                                icon={mdiLanDisconnect}
                                title="Forçar desconexão/reconexão"
                                onClick={() => disconnectMutator.mutate(row.id)}
                                disabled={!row.connected}
                            />
                        </>)}
                        <Botao
                            variant="none-info"
                            icon={mdiPlayCircleOutline}
                            title="Testar ações"
                            onClick={() => {
                                setTestActionIntegration(row);
                                setShowTestActionModal(true);
                            }}
                            disabled={!row.connected}
                        />
                    </>)}
                    {/* {row.template_key !== "custom" && (
                        <Botao
                            variant="none-secondary"
                            icon={mdiRefresh}
                            title="Alterar versão"
                            onClick={() => handleChangeVersion(row)}
                        />
                    )} */}
                    <Botao
                        variant="none-primary"
                        icon={mdiPen}
                        title="Editar"
                        onClick={() => handleEdit(row)}
                    />
                    <Botao
                        variant="none-info"
                        icon={mdiFileDocument}
                        title="Ver logs"
                        onClick={() => {
                            setSelectedIntegrationId(row.id);
                            setShowLogsModal(true);
                        }}
                    />
                    <BotaoPerigoso
                        variant="none-danger"
                        icon={mdiTrashCan}
                        title="Excluir"
                        onClick={() => handleDelete(row.id)}
                    />
                </HBox>
            ),
        },
    ];

    const [showFreeTierModal, setShowFreeTierModal] = React.useState(false);

    return (
        <LayoutUsuario>
            <FreeTierModal
                show={showFreeTierModal}
                onClose={() => setShowFreeTierModal(false)}
                feature="a criação ou edição de integrações"
            />

            {/* Modal para edição/criação */}
            <Modal
                size="lg"
                show={editObject !== null}
                title={editObject?.id ? "Editar Integração" : "Nova Integração"}
                onClose={() => {
                    setEditObject(null);
                    setFieldErrors(null);
                }}
                onCancel={() => {
                    setEditObject(null);
                    setFieldErrors(null);
                }}
                onConfirm={handleSave}
                confirmProps={{
                    label: 'Salvar',
                }}
            >
                <FormIntegracaoApi
                    value={editObject}
                    onChange={setEditObject}
                    fieldErrors={fieldErrors || {}}
                    availableTemplates={availableTemplates}
                />
            </Modal>

            <VBox gap="16px" style={{ height: "calc(100vh - 32px)" }}>
                {/* Header */}
                <Grid>
                    <GridItem sm={12} md={6}>
                        <VBox>
                            <Titulo>Catálogo de Integrações</Titulo>
                            <Descricao>
                                Configure e gerencie integrações com sistemas externos.
                            </Descricao>
                        </VBox>
                    </GridItem>
                    <GridItem sm={12} md={6}>
                        <HBox gap="12px" className={styles.headerAcoes}>
                            <Botao
                                variant="outline-info"
                                onClick={() => {
                                    setSelectedIntegrationId(null);
                                    setShowLogsModal(true);
                                }}
                                icon={mdiFileDocument}
                            >
                                Logs de Integrações
                            </Botao>

                            <div>
                                <Botao
                                    variant="primary"
                                    icon={mdiPlus}
                                    onClick={handleNewIntegration}
                                >
                                    Nova Integração
                                </Botao>
                                {capacidadesQuery.data?.freeTier && (
                                    <div
                                        style={{
                                            marginLeft: '8px',
                                            fontSize: '12px',
                                            fontStyle: 'italic',
                                            color: 'var(--tc-color-danger)',
                                            textAlign: 'center',
                                        }}
                                    >
                                        Plano gratuito
                                    </div>
                                )}
                            </div>
                        </HBox>
                    </GridItem>
                </Grid>

                {/* Filtros */}
                <HBox>
                    <CampoTexto
                        value={search}
                        onChange={setSearch}
                        prepend={
                            <Icon
                                path={mdiMagnify}
                                size={1}
                                color="var(--cor-texto-secundaria)"
                            />
                        }
                        placeholder="Buscar..."
                        style={{ flex: 1 }}
                    />

                    <Botao
                        icon={mdiRefresh}
                        variant="outline-secondary"
                        onClick={() => queryList.refetch()}
                        disabled={queryList.isFetching}
                    >
                        {queryList.isFetching && (
                            <Icon path={mdiLoading} size={0.8} spin={true} />
                        )}
                        Atualizar
                    </Botao>
                </HBox>

                {/* Lista de integrações */}
                {queryList.isFetching && (
                    <div>
                        Aguarde...{' '}
                        <Icon path={mdiLoading} size={0.8} color="var(--tc-color-gray-700)" spin />
                    </div>
                )}
                {!queryList.isFetching && (
                    <div>
                        Exibindo {integrations?.length ?? '0'} de {queryList.data?.pages?.[queryList.data.pages.length - 1]?.total ?? '0'}{' '}
                        resultados.
                    </div>
                )}

                <DataTable
                    isLoading={queryList.isFetching}
                    cabecalhos={headers}
                    linhas={integrations ?? []}
                    hasMoreData={!!queryList.hasNextPage}
                    onLoadMore={async () => queryList.fetchNextPage()}
                />

                {/* Modal de logs de integrações */}
                <IntegrationLogsModal
                    isOpen={showLogsModal}
                    onClose={() => setShowLogsModal(false)}
                    integrationId={selectedIntegrationId}
                />

                {/* Modal de teste de ações */}
                <IntegrationTestActionModal
                    isOpen={showTestActionModal}
                    onClose={() => {
                        setShowTestActionModal(false);
                        setTestActionIntegration(null);
                    }}
                    integration={testActionIntegration}
                />
            </VBox>
        </LayoutUsuario>
    );
};

export default PageIntegracao;
